import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom-v5-compat";
import {
  useFetchCarItineraryQuery,
  type CarReservationDetailPageProps,
} from "@apac/components/cars";
import {
  setSupportProductInfo,
  TravelCategory,
} from "@b2bportal/core-self-serve";
import { fetchPointsBalance } from "@/modules/account/thunks/fetchPointsBalance";
import { useCarCancelPolicy } from "@/modules/cars-postbooking/hooks";
import { carsPostBookingApi } from "@/modules/cars-postbooking/store";

export const useCarReservationDetailPage = (
  props: CarReservationDetailPageProps
) => {
  const params = useParams<{ reservationId: string }>();
  const dispatch = useDispatch();

  const {
    data,
    isLoading: isFetchCarQueryLoading,
    isError,
    refetch,
  } = useFetchCarItineraryQuery({
    itineraryId: params.reservationId ?? "",
  });

  const itinerary = data?.itinerary;

  const [showCancellation, setShowCancellation] = useState(false);

  const {
    getPolicyInfo,
    cancelFlowStep,
    cancelScenario,
    isQuoteLoading,
    isFulfilling,
    handleGroundCancel,
  } = useCarCancelPolicy({
    itinerary,
    onFulfillSuccess: () => {
      refetch();
      // Points refunded after cancellation
      dispatch(fetchPointsBalance({ notifyNativeApp: true }));
      // Invalidate cached trips list so it refetches with updated status
      dispatch(carsPostBookingApi.util.invalidateTags(["itineraries"]));
    },
  });

  useEffect(() => {
    dispatch(
      setSupportProductInfo(
        params.reservationId !== undefined
          ? {
              productId: params.reservationId,
              productType: TravelCategory.Ground,
            }
          : undefined
      )
    );

    return () => {
      dispatch(setSupportProductInfo());
    };
  }, [dispatch, params.reservationId]);

  const onQueryCancellation = useCallback(() => {
    getPolicyInfo();
    setShowCancellation(true);
  }, [getPolicyInfo]);

  const onExitCancellation = useCallback(() => {
    setShowCancellation(false);
  }, []);

  const isLoading =
    isFetchCarQueryLoading || (showCancellation && isQuoteLoading);

  return useMemo(
    () => ({
      context: {
        isLoading,
        isQuoteLoading,
        isFulfilling,
        isError,
        itinerary,
        showCancellation,
        cancelFlowStep,
        cancelScenario,
      },
      handlers: {
        onQueryCancellation,
        onExitCancellation,
        refetchCurrentBooking: refetch,
        handleGroundCancel,
        getPolicyInfo,
      },
    }),
    [
      isLoading,
      isQuoteLoading,
      isFulfilling,
      isError,
      itinerary,
      showCancellation,
      cancelFlowStep,
      cancelScenario,
      onQueryCancellation,
      onExitCancellation,
      refetch,
      handleGroundCancel,
      getPolicyInfo,
    ]
  );
};
